import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { phoneLast9 } from '@/utils/phone';

export interface MatchingCustomer {
  id: string;
  customer_name: string;
  company_name: string | null;
  primary_phone: string;
  primary_email: string | null;
  primary_address: string;
  total_jobs_count: number | null;
  /** Which of the typed fields hit this customer. */
  matched_on: 'phone' | 'email' | 'phone+email';
}

/**
 * Existing customers that own the phone number or email a CSR is typing into
 * the lead form. The database links a new lead by phone (and, from the
 * 2026-09-16 migration, by email) on insert; this is the same rule surfaced
 * live, so the CSR can see the match and choose it before saving.
 */
export function useMatchingCustomers(
  phone: string | null | undefined,
  email: string | null | undefined,
  opts?: { enabled?: boolean },
) {
  const p9 = phoneLast9(phone);
  const phoneOk = !!p9 && p9.length >= 8;
  const em = (email ?? '').trim().toLowerCase();
  // A partial address like "sh" must not query; wait for something addressable.
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em);
  const enabled = (opts?.enabled ?? true) && (phoneOk || emailOk);

  return useQuery({
    queryKey: ['matching-customers', phoneOk ? p9 : '', emailOk ? em : ''],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<MatchingCustomer[]> => {
      const ors: string[] = [];
      if (phoneOk) ors.push(`phone_last9.eq.${p9}`, `secondary_phone_last9.eq.${p9}`);
      if (emailOk) ors.push(`normalized_email.eq.${em}`);

      // One literal, not a concatenation: supabase-js derives the row type by
      // parsing this string, and a runtime expression makes every row untyped.
      const { data, error } = await supabase
        .from('customers')
        .select('id, customer_name, company_name, primary_phone, primary_email, primary_address, phone_last9, secondary_phone_last9, normalized_email, total_jobs_count')
        .or(ors.join(','))
        .order('created_at', { ascending: true })
        .limit(5);
      if (error) throw error;

      return (data ?? []).map((c) => {
        const byPhone = phoneOk && (c.phone_last9 === p9 || c.secondary_phone_last9 === p9);
        const byEmail = emailOk && c.normalized_email === em;
        return {
          id: c.id,
          customer_name: c.customer_name,
          company_name: c.company_name,
          primary_phone: c.primary_phone,
          primary_email: c.primary_email,
          primary_address: c.primary_address,
          total_jobs_count: c.total_jobs_count,
          matched_on: byPhone && byEmail ? 'phone+email' : byPhone ? 'phone' : 'email',
        };
      });
    },
  });
}
